import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
// Distinct fill cues per platform so the operator can tell by ear
// which platform just filled without looking at the row. Each fill
// event plays the clip ONCE (no loop); rapid bursts are throttled by
// EXECUTION_CLIP_MS so we never restart a clip mid-play. When both
// platforms fill in the same cycle the clips overlap on independent
// players — the "both sides are moving right now" signal.
import kalshiFillSound from 'src/Sounds/kalshi-fill.mp3';
import polyFillSound from 'src/Sounds/poly-fill.mp3';
// Distinct alert tone for errors / manual-intervention events.
// Re-uses the existing doorbell clip — was the previous full-fill
// sound, freed up when fills migrated to the cash register loop.
import alarmSound from 'src/Sounds/error.mp3';
// Single beep for TNA-negative crossings: the entry-side TNA just
// crossed below the profit line on a pair we hold — an OPPORTUNITY
// to act on, not an error. Lighter than the alarm doorbell (errors)
// and the double-beep (the prior "alert" framing), so the operator
// hears "heads up, look at this" without it sounding like a fault.
import tnaSound from 'src/Sounds/beep.mp3';
import type {
  NotificationEvent,
  NotificationEventType
} from '../types/notifications.types';

/**
 * Three audio tiers for prediction-market notifications.
 *
 *   1. **Alarm** — errors and manual-intervention events. Plays a
 *      distinct doorbell tone, throttled by ALARM_COOLDOWN_MS so a
 *      flurry of related errors doesn't blast the operator.
 *
 *   2. **Execution** — every fill, partial fill, and cycle-complete
 *      event, regardless of origin: auto-unwinder, auto-opener
 *      (direction === 'entry'), AND manual operator orders. SPLIT BY
 *      PLATFORM:
 *      Kalshi fills play `kalshi-fill.mp3` on a dedicated burst-
 *      coalesced loop, Polymarket fills play `poly-fill.mp3` on a
 *      separate dedicated burst-coalesced loop. The two loops are
 *      independent and can overlap — when both platforms fill in
 *      the same cycle the operator hears both rhythms, which is the
 *      exact "both sides are moving" signal we want.
 *
 *      Manual fills (`manual_order_executed`) ride this same
 *      per-platform cue rather than a separate achievement chime — the
 *      operator wanted ONE consistent "platform X just filled" sound
 *      across every fill source, automation or manual. Per-chunk
 *      partials on a still-resting manual order
 *      (`manual_order_partial_fill`) ride the SAME cue too; the 🟡 chip
 *      is the visual differentiator, audio stays consistent so a
 *      partial chunk isn't silently dropped.
 *
 *   3. **TNA** — a position's taker-side TNA just went negative
 *      (only fires when combined contracts ≥ 11; the producer
 *      gates that). Plays a distinct double-beep so the operator
 *      can tell "a real position went underwater" by ear, separately
 *      from outright errors.
 *
 * Priority order (top wins): **alarm > execution > tna**. Rationale:
 * a busy engine producing fills is the loudest live indicator that
 * everything is working — we DON'T want a TNA dip cue to interrupt
 * the cash-register loop. The operator hears "it is making money"
 * continuously; alarms still punch through as the only "stop and
 * look" signal. TNA defers to execution.
 *
 * Anything not in any set stays silent — the row, color, and Slack
 * post are still there, just no sound. Avoids alarm fatigue on
 * lifecycle / digest / recovery-success events.
 */
// Fills route to the per-platform cash-register loop regardless of
// origin: auto-unwinder, auto-opener (direction === 'entry'), AND
// manual operator orders. The operator wants ONE consistent "platform X just filled"
// audio signal — no separate chime for manual fills, no silence for
// opener fills. `manual_order_placed` (the click ack) stays unlisted
// because the click itself already confirmed it.
// Base event types only — the entry-vs-unwind axis now lives on
// `event.direction`, not the type name, so a single base entry covers
// both the opener (buy-side) and unwinder (sell-side) fill of the same
// kind. The cue is identical for both directions anyway (the operator
// wants ONE "platform X just filled" signal regardless of side).
const EXECUTION_EVENT_TYPES = new Set<NotificationEventType>([
  // Full fills
  'maker_filled',
  'counter_filled',
  'recovery_filled',
  'crossing_executed',
  // Partial fills
  'maker_partial_fill',
  'partial_fill_accumulated',
  'crossing_partial',
  'crossing_accumulated',
  // Cycle done
  'execution_complete',
  // Manual operator-placed fill — operator wanted the same per-platform
  // fill sound as automation fills, no separate chime. Partial chunks on
  // a still-resting manual order ring the same per-platform cue as the
  // full fill — the 🟡 chip is the only thing that distinguishes them
  // visually, and the operator wants identical audio so a per-trade
  // partial isn't silent.
  'manual_order_executed',
  'manual_order_partial_fill'
]);

/**
 * Events that demand operator attention — every one of these is
 * either a leg-risk situation, a platform that just got disabled,
 * or a system halt the operator must clear by hand.
 *
 * `recovery_filled` is special-cased below: it's an execution event
 * when the counter is `executing` (recovery in progress, no
 * intervention needed) but an alarm when `failed` or `no_bid`
 * (operator must hedge manually).
 */
// `emergency_stop` is intentionally NOT in
// this set: panic is operator-initiated (severity `warning`, source
// `manual`) so the doorbell would be triple-redundant — the operator
// just clicked the button. Genuine engine faults (`error`,
// `counter_failed`, `out_of_balance`, `platform_disconnect`) still
// alarm.
const ALARM_EVENT_TYPES = new Set<NotificationEventType>([
  'error',
  'counter_failed',
  'out_of_balance',
  'platform_disconnect'
]);

/**
 * Per-platform "play in flight" window. Each fill event plays its
 * platform's clip ONCE (no loop) — subsequent fills arriving inside
 * this window are silently dropped so we don't restart the clip
 * mid-play and produce stutter. Tuned to a comfortable upper bound
 * over the actual clip durations measured with `afinfo`:
 *   - `kalshi-fill.mp3` ≈ 365 ms
 *   - `poly-fill.mp3`   ≈ 648 ms
 * 800 ms covers both with margin. If you swap a longer clip in,
 * bump this. If you swap a shorter clip in, leave it — the throttle
 * only suppresses extra plays, it never delays an idle one.
 *
 * Historical context: this used to be `TAIL_MS = 1500` with
 * `loop: true`, which played the OLD `cash-register.mp3` (~1 s) about
 * 1.5 times per fill — read as one continuous burst when fills
 * stacked. The new shorter clips made a single fill loop 3-4 times
 * (2026-05-23 bug). Switched to one-clip-per-fill + throttle.
 */
const EXECUTION_CLIP_MS = 800;

/**
 * Minimum gap between alarm sounds. Several errors firing within
 * this window play ONE alarm — the page row + Slack post still
 * surface every event, but the audio cue isn't repeated. Tuned
 * short enough that a genuine new error 5 s later still alerts.
 */
const ALARM_COOLDOWN_MS = 3000;

/** How long an alarm sound plays before auto-stopping. */
const ALARM_PLAY_MS = 3000;

/**
 * Minimum gap between TNA cues. Sign-flip dedupe at the producer
 * (`TnaAlertService.lastSign`) already prevents repeated firings
 * on a sustained-negative pair, but two different pairs flipping
 * within the cooldown still play one cue — the rows + Slack post
 * still surface every event, the audio is the only deduped surface.
 */
const TNA_COOLDOWN_MS = 3000;

/** How long the TNA cue plays before auto-stopping. */
const TNA_PLAY_MS = 2000;

type Tier = 'execution' | 'tna' | 'alarm';
type ExecutionPlatform = 'kalshi' | 'polymarket';

function classifyEvent(event: NotificationEvent): Tier | null {
  if (ALARM_EVENT_TYPES.has(event.type)) return 'alarm';
  if (event.type === 'recovery_filled') {
    // Recovery "executing" = automation is hedging the leg, no
    // operator action needed → execution tier (cash register).
    // Recovery "failed" / "no_bid" = manual intervention → alarm.
    // Covers both directions (the type is direction-agnostic now).
    const counterAction = (event.payload as any)?.counterAction;
    return counterAction === 'executing' ? 'execution' : 'alarm';
  }
  // TNA crossings only fire from the producer when combined contracts
  // ≥ 11 — at this layer we trust that gate and just route to the
  // distinct cue.
  if (event.type === 'tna_negative') return 'tna';
  if (EXECUTION_EVENT_TYPES.has(event.type)) return 'execution';
  return null;
}

/**
 * Resolve the platform an execution event is about, so we can route
 * to the right per-platform cash-register loop. Reads `event.platform`
 * first (set by the producer on every fill notification — see
 * `slack-notification.service.ts`'s `notifyMakerFilled` /
 * `notifyCounterFilled` / `notifyCrossingExecuted` etc.), and falls
 * back to payload fields for legacy events that didn't thread it
 * through. Returns `null` for events with no resolvable platform
 * (notably `execution_complete`) — the caller
 * extends whichever player is already running instead of starting
 * one arbitrarily, so a cycle-end summary on its own stays silent.
 */
function resolveExecutionPlatform(
  event: NotificationEvent
): ExecutionPlatform | null {
  const candidates: unknown[] = [
    event.platform,
    (event.payload as any)?.platform,
    (event.payload as any)?.counterPlatform,
    (event.payload as any)?.makerPlatform
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const lc = c.toLowerCase();
    if (lc === 'kalshi' || lc === 'polymarket') return lc;
  }
  return null;
}

/**
 * Plays a per-platform fill loop for execution events and a doorbell
 * alarm for errors / manual-intervention events. Designed around the
 * operator's reality: a quiet engine produces few sounds, a busy cycle
 * produces ONE rolling fill loop per platform, and any error that
 * genuinely needs eyes punches through with a distinct alarm.
 *
 * Pass `null`/`undefined` for ticks where the event has already been
 * consumed (`useNotificationsSocket.acknowledge()`). The hook also
 * guards on `event.id` so an unrelated re-render doesn't replay the
 * cue.
 */
export function useNotificationSound(
  event: NotificationEvent | null | undefined
): void {
  // Main player carries the mutually-exclusive non-execution cues
  // (alarm, TNA beep). Execution gets its own per-platform players
  // below so a Kalshi fill loop and a Poly fill loop can overlap
  // without fighting over a single audio handle.
  //
  // IMPORTANT: `useAudioPlayer()` returns a NEW wrapper object every
  // render (it spreads internal `state` into the return value, and
  // state updates fire on every audio event). The INNER `load`/`stop`
  // callbacks are stable (wrapped in `useCallback([])` inside the
  // lib), so we destructure them up front and use only those stable
  // refs in the effect dep arrays. Storing the wrapper objects
  // directly silently stopped every sound on each render because the
  // cleanup effect's dep changed each tick.
  const { load: loadMain, stop: stopMain } = useAudioPlayer();
  const { load: loadKalshi, stop: stopKalshi } = useAudioPlayer();
  const { load: loadPoly, stop: stopPoly } = useAudioPlayer();
  const lastPlayedId = useRef<string | null>(null);

  // Currently-active tier on `mainPlayer` so an execution loop
  // doesn't clobber an in-progress alarm and a stale event re-render
  // doesn't restart an unrelated sound. The per-platform execution
  // players track their own burst state via the *StartedAt refs
  // below, so they don't need to read this.
  const currentTier = useRef<Tier | null>(null);

  // Per-platform "play in flight until" timestamp (Date.now() ms).
  // 0 = idle. Set on each load() to `now + EXECUTION_CLIP_MS` and
  // checked before starting a new play so rapid bursts don't restart
  // the clip mid-play (stutter). Reset to 0 by `stopAllExecution()`
  // when an alarm cuts the play short.
  const kalshiPlayingUntil = useRef(0);
  const polyPlayingUntil = useRef(0);

  // Alarm cooldown — last time an alarm sound started.
  const lastAlarmAt = useRef(0);

  // TNA cooldown — last time a TNA cue started. Independent from
  // the alarm cooldown so a flurry of TNA alerts on different pairs
  // doesn't get throttled by an unrelated alarm cooldown window.
  const lastTnaAt = useRef(0);

  // Single timer for the main player's tier (alarm / tna).
  const mainStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!event) return;
    if (event.id === lastPlayedId.current) return;
    const tier = classifyEvent(event);
    if (!tier) return;

    lastPlayedId.current = event.id;
    const now = Date.now();

    // Helpers — inlined so the effect's dep array stays accurate
    // (every callback these read is one the effect depends on).
    const stopAllExecution = (): void => {
      kalshiPlayingUntil.current = 0;
      stopKalshi();
      polyPlayingUntil.current = 0;
      stopPoly();
    };
    const isAnyExecutionRunning = (): boolean =>
      now < kalshiPlayingUntil.current || now < polyPlayingUntil.current;

    if (tier === 'alarm') {
      // Alarm cooldown — silent if another alarm just fired.
      if (now - lastAlarmAt.current < ALARM_COOLDOWN_MS) return;
      lastAlarmAt.current = now;

      // Interrupt any execution loop / tna beep in progress —
      // alarms have priority. Operator should hear the doorbell
      // over a running fill loop.
      stopAllExecution();
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      currentTier.current = 'alarm';
      try {
        loadMain(alarmSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      mainStopTimer.current = setTimeout(() => {
        stopMain();
        currentTier.current = null;
        mainStopTimer.current = null;
      }, ALARM_PLAY_MS);
      return;
    }

    if (tier === 'tna') {
      // Cooldown so two pairs flipping at once play one cue.
      if (now - lastTnaAt.current < TNA_COOLDOWN_MS) return;
      lastTnaAt.current = now;

      // Defer to alarms AND to any execution fill loop — the
      // operator wants to keep hearing the loop while the engine
      // is making money.
      if (currentTier.current === 'alarm') return;
      if (isAnyExecutionRunning()) return;
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      currentTier.current = 'tna';
      try {
        loadMain(tnaSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      mainStopTimer.current = setTimeout(() => {
        stopMain();
        currentTier.current = null;
        mainStopTimer.current = null;
      }, TNA_PLAY_MS);
      return;
    }

    // tier === 'execution' — defer only to alarms. Interrupts the
    // TNA cue so the fill clip is the dominant audio signal during
    // a busy engine cycle.
    if (currentTier.current === 'alarm') return;

    // One discrete play per fill event (no loop). The per-platform
    // `playingUntil` guard skips additional fills arriving while the
    // clip is still in flight, so we never restart the clip mid-play.
    // Inlined into the effect (rather than a separate helper) so the
    // dep array stays accurate — every player + ref read here is one
    // the effect already depends on.
    const playExecutionFill = (
      platform: ExecutionPlatform,
      tNow: number
    ): void => {
      const playingUntil =
        platform === 'kalshi' ? kalshiPlayingUntil : polyPlayingUntil;
      if (tNow < playingUntil.current) return;
      const load = platform === 'kalshi' ? loadKalshi : loadPoly;
      const src = platform === 'kalshi' ? kalshiFillSound : polyFillSound;
      try {
        load(src, { autoplay: true, loop: false });
      } catch {
        return;
      }
      playingUntil.current = tNow + EXECUTION_CLIP_MS;
    };

    // Cycle-end summaries (`execution_complete`)
    // have no resolvable platform — the maker/counter fills inside
    // the cycle already triggered their own clip plays, so the
    // summary on its own stays silent.
    const platform = resolveExecutionPlatform(event);
    if (platform === null) return;

    // If a TNA beep owns the main player, evict it — execution
    // outranks it. Per-platform fills overlap each other freely
    // (they're on independent players), so we don't touch the
    // OTHER platform's player when this one starts.
    if (currentTier.current === 'tna') {
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      mainStopTimer.current = null;
      stopMain();
      currentTier.current = null;
    }

    playExecutionFill(platform, now);
  }, [
    event,
    loadMain,
    stopMain,
    loadKalshi,
    stopKalshi,
    loadPoly,
    stopPoly,
  ]);

  // Cleanup on unmount — stop every player so no audio handle
  // outlives the page navigation. Deps are the stable `stop`
  // callbacks (NOT the player wrapper objects) so this only
  // fires on unmount, not on every render.
  useEffect(() => {
    return () => {
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      stopMain();
      stopKalshi();
      stopPoly();
    };
  }, [stopMain, stopKalshi, stopPoly]);
}
